import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Spinner, Tab, Tabs } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { ShieldCheck, X, AlertTriangle, Check } from "lucide-react";
import { auditApp, auditServer } from "../../redux/actionCreator";

/**
 * Post-deployment audit — security pen-test surface + code-standards
 * checks. Two tabs:
 *
 *   System  — ssh + firewall + ssh-hardening + outdated-packages +
 *             fail2ban + lynis + open ports rolled up into findings.
 *             Runs against the server selected on the row.
 *
 *   Apps    — per-app code quality: npm audit, npm outdated,
 *             package.json scripts presence, repo size. Lists each
 *             pm2_node app on the project so the operator can audit
 *             them in turn without leaving the modal.
 *
 * Each finding has a severity (critical / warn / ok). The summary at
 * the top shows a quick rollup so the operator can decide whether to
 * act before reading the per-finding detail.
 */
export default function AuditPanel({ show, onHide, project, server, apps = [] }) {
  const projectId = project?._id;
  const serverId = server?._id;
  const [tab, setTab] = useState("system");
  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0 d-inline-flex align-items-center gap-2"><ShieldCheck size={18} /> Audit — {server?.label || project?.project_name || "project"}</h5>
      </Modal.Header>
      <Modal.Body>
        <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-3">
          <Tab eventKey="system" title="System security">
            {projectId && serverId ? (
              <SystemAuditTab projectId={projectId} serverId={serverId} server={server} />
            ) : (
              <div className="text-muted">Pick a server to audit.</div>
            )}
          </Tab>
          <Tab eventKey="apps" title="App quality">
            {projectId ? (
              <AppAuditTab projectId={projectId} apps={apps} />
            ) : (
              <div className="text-muted">No project context.</div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ───────────────── System audit ───────────────── */

function SystemAuditTab({ projectId, serverId, server }) {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = useCallback(async () => {
    setLoading(true); setErr(null); setData(null);
    const res = await dispatch(auditServer(projectId, serverId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Audit failed");
  }, [dispatch, projectId, serverId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">
          Single SSH probe — checks open ports, firewall, sshd hardening, fail2ban, package updates, failed-login burst, lynis (if installed), and world-writable /etc files. Read-only.
        </small>
        <Button size="sm" variant="light" onClick={run} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : "↻ Re-scan"}
        </Button>
      </div>
      {err && <div className="alert alert-danger small">{err}</div>}
      {loading && !data ? (
        <div className="text-center py-4"><Spinner animation="border" /> Scanning {server?.label || serverId}…</div>
      ) : !data ? null : (
        <>
          <SummaryBar rollup={data.rollup} />
          {data.os && (
            <div className="small text-muted mb-2">
              <code>{data.os}</code>
              {data.uptime && <> · {data.uptime}</>}
            </div>
          )}
          <FindingsList findings={data.findings} />
          {data.raw?.ports?.length > 0 && (
            <details className="mt-3">
              <summary className="small text-muted">Raw `ss -tlnp` output ({data.raw.ports.length} listeners)</summary>
              <pre className="small mt-1 p-2" style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 4, maxHeight: 240, overflowY: "auto" }}>
                {data.raw.ports.join("\n")}
              </pre>
            </details>
          )}
        </>
      )}
    </div>
  );
}

/* ───────────────── App audit (npm audit + outdated + scripts) ─────── */

function AppAuditTab({ projectId, apps }) {
  const dispatch = useDispatch();
  const pm2Apps = (apps || []).filter((a) => a.mode === "pm2_node");
  const [selected, setSelected] = useState(pm2Apps[0]?._id || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = useCallback(async (appId) => {
    if (!appId) return;
    setLoading(true); setErr(null); setData(null);
    const res = await dispatch(auditApp(projectId, appId));
    setLoading(false);
    if (res?.status) setData(res.data);
    else setErr(res?.message || "Audit failed");
  }, [dispatch, projectId]);

  useEffect(() => { if (selected) run(selected); }, [selected, run]);

  if (!pm2Apps.length) {
    return (
      <div className="alert alert-info small">
        Per-app audit currently supports <code>pm2_node</code> apps. None on this project yet.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex gap-2 align-items-end mb-2">
        <div style={{ flex: 1, maxWidth: 360 }}>
          <label className="form-label small mb-1">Application</label>
          <select
            className="form-select form-select-sm"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={loading}
          >
            {pm2Apps.map((a) => (
              <option key={a._id} value={a._id}>
                {a.name} {a.container_name ? `(${a.container_name})` : ""}
              </option>
            ))}
          </select>
        </div>
        <Button size="sm" variant="light" onClick={() => run(selected)} disabled={loading || !selected}>
          {loading ? <Spinner size="sm" animation="border" /> : "↻ Re-scan"}
        </Button>
      </div>
      <small className="text-muted d-block mb-2">
        Runs <code>npm audit --json</code> + <code>npm outdated --json</code> in the
        app's repo_dir on its first target server. Read-only — no installs, no edits.
      </small>
      {err && <div className="alert alert-danger small">{err}</div>}
      {loading && !data ? (
        <div className="text-center py-4"><Spinner animation="border" /></div>
      ) : !data ? null : data.skipped ? (
        <div className="alert alert-info small">{data.reason}</div>
      ) : (
        <>
          <SummaryBar rollup={data.rollup} />
          <div className="small text-muted mb-2">
            {data.package?.name && (
              <>
                <code>{data.package.name}@{data.package.version}</code> ·{" "}
              </>
            )}
            on <code>{data.server_label}</code> · repo {data.repo_size || "?"} · node_modules {data.node_modules_size || "?"}
          </div>
          {data.vulnerabilities && (
            <div className="row g-1 mb-3">
              {["critical", "high", "moderate", "low"].map((sev) => (
                <div key={sev} className="col">
                  <div className={`text-center py-2 rounded`} style={{
                    background: sev === "critical" ? "#fef2f2" : sev === "high" ? "#fff7ed" : sev === "moderate" ? "#fefce8" : "#f0fdf4",
                    border: `1px solid ${sev === "critical" ? "#fca5a5" : sev === "high" ? "#fdba74" : sev === "moderate" ? "#fde68a" : "#86efac"}`,
                  }}>
                    <strong style={{ fontSize: 18 }}>{data.vulnerabilities[sev] || 0}</strong>
                    <div className="small text-muted">{sev}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <FindingsList findings={data.findings} />
          {data.outdated_sample?.length > 0 && (
            <details className="mt-3">
              <summary className="small text-muted">Outdated packages (top 10 of {data.outdated_count})</summary>
              <table className="emp-table mt-1">
                <thead>
                  <tr><th>Package</th><th>Current</th><th>Wanted</th><th>Latest</th></tr>
                </thead>
                <tbody>
                  {data.outdated_sample.map((o) => (
                    <tr key={o.name}>
                      <td><code className="small">{o.name}</code></td>
                      <td className="small">{o.current || "—"}</td>
                      <td className="small">{o.wanted || "—"}</td>
                      <td className="small">{o.latest || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
    </div>
  );
}

/* ───────────────── Shared bits ───────────────── */

function SummaryBar({ rollup }) {
  if (!rollup) return null;
  const total = (rollup.critical || 0) + (rollup.warn || 0) + (rollup.ok || 0);
  if (!total) return null;
  return (
    <div className="d-flex gap-2 mb-3">
      <span className="badge bg-danger">{rollup.critical || 0} critical</span>
      <span className="badge bg-warning text-dark">{rollup.warn || 0} warn</span>
      <span className="badge bg-success">{rollup.ok || 0} ok</span>
    </div>
  );
}

function FindingsList({ findings }) {
  if (!findings?.length) return <div className="text-muted small">No findings.</div>;
  const sevColor = (s) => s === "critical" ? "danger" : s === "warn" ? "warning" : "success";
  const sevIcon = (s) => s === "critical" ? <X size={12} /> : s === "warn" ? <AlertTriangle size={12} /> : <Check size={12} />;
  return (
    <div>
      {findings.map((f, i) => (
        <div key={i} className={`d-flex gap-2 mb-2 p-2 rounded border-start border-${sevColor(f.severity)}`} style={{ borderLeftWidth: "4px !important", background: "#fafafa" }}>
          <div style={{ width: 22, fontWeight: "bold", color: f.severity === "critical" ? "#dc3545" : f.severity === "warn" ? "#f59e0b" : "#22c55e" }}>
            {sevIcon(f.severity)}
          </div>
          <div className="flex-grow-1">
            <div>{f.title}</div>
            {f.detail && (
              <pre className="small text-muted mb-0 mt-1" style={{ whiteSpace: "pre-wrap", maxHeight: 120, overflowY: "auto" }}>
                {f.detail}
              </pre>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
